import { useCallback, useMemo, useState } from 'react';
import { fromDisplayConfig } from '../utils/displaySettings.js';
import {
  restoreDatasetSlotAssignments,
  restoreSlotAssignments,
} from '../utils/restoreSlotAssignments.js';
import { parseNonFieldBindings } from '../utils/nonFieldBindings.js';

import { AGGREGATE_WITHOUT_FIELD } from '../constants/widgetContract.js';
import { FIRST_WIDGET_BUILDER_TAB } from '../constants/widgetBuilderTabs.js';
import { OPERATORS_WITHOUT_VALUE } from '../constants/filterOperators.js';

/** Operations the Data step offers on the entity path (CT-5194). */
export const WIDGET_OPERATIONS = Object.freeze(['summarize', 'record_set']);

/**
 * What a source change wipes out. Named so the warning can list it. The widget
 * type is deliberately absent: since the type list stopped being gated by the
 * data shape, a chart choice survives a change of source.
 */
export const DOWNSTREAM_CONFIG_KEYS = Object.freeze([
  'filters',
  'exposedColumns',
]);

/** What switching Summarize ↔ Record Set wipes out. The filters survive. */
export const OPERATION_CONFIG_KEYS = Object.freeze([
  'measures',
  'groupers',
  'exposedColumns',
]);

const EMPTY_DATASET = Object.freeze({
  sourceType: '',
  sourceId: '',
  // Display metadata travels with the selection: the live preview's meta line
  // needs the entity's name and integration, and re-querying the catalog for
  // two strings the picker already had would be silly.
  sourceLabel: '',
  sourceIntegration: '',
  datasetId: '',
  datasetShape: '',
  entityRef: '',
  // The composite the widget is actually stored against. The picker shows the
  // member entity; `create` takes the composite and validates every field uuid
  // against it, so the selection has to carry both.
  compositeEntityUuid: '',
  operationType: '',
  filters: Object.freeze([]),
  /*
    What the summary measures. Empty means the row count, which is what the
    builder sent before there was a picker and what most widgets still want
    (CT-5641).
  */
  measures: Object.freeze([]),
  groupers: Object.freeze([]),
  // `null` means "nothing picked yet". The user chooses columns explicitly.
  exposedColumns: null,
});

const EMPTY_SLOTS = Object.freeze({});

/**
 * A measure counts once it has an operation and, unless it counts rows, a
 * column. An unfinished row sits in the UI and stays out of the payload, the
 * same contract the filter rows have.
 */
export const isCompleteMeasure = (measure) => {
  if (!measure?.aggregate) return false;
  return measure.aggregate === AGGREGATE_WITHOUT_FIELD || Boolean(measure.column);
};

/** A filter counts only once it has a column, an operator and (if needed) a value. */
export const isCompleteFilter = (filter) => {
  if (!filter?.column || !filter?.operator) return false;
  if (OPERATORS_WITHOUT_VALUE.includes(filter.operator)) return true;
  if (filter.operator === 'between') {
    return filter.value !== '' && filter.value != null
      && filter.valueTo !== '' && filter.valueTo != null;
  }
  return filter.value !== '' && filter.value != null;
};

/**
 * Which columns a `record_set` exposes.
 *
 * `null` means **nothing picked yet**, not "all of them": the design source of
 * truth moved on 2026-09-08 and the section now opens on `No columns selected ·
 * Pick the fields this widget will show`, with the picker starting empty.
 *
 * @param {string[]|null} exposedColumns The form's raw value.
 */
export const resolveExposedColumns = (exposedColumns) => exposedColumns ?? [];

/**
 * Row ids are local React keys, re-issued on every hydration, so a snapshot that
 * kept them would make a freshly loaded form differ from itself.
 */
const withoutRowIds = (dataset) => ({
  ...dataset,
  filters: dataset.filters.map(({ id, ...rest }) => rest),
  measures: (dataset.measures ?? []).map(({ id, ...rest }) => rest),
  groupers: dataset.groupers.map(({ id, ...rest }) => rest),
});

const rowId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

const resolveDataShape = (dataset) => {
  if (dataset.sourceType === 'dataset') return dataset.datasetShape || null;
  if (!dataset.operationType) return null;
  if (dataset.operationType === 'record_set') return 'record_set';
  return dataset.groupers.length ? 'grouped' : 'single_value';
};

const hasDownstreamConfig = (dataset) =>
  dataset.filters.length > 0
  || (dataset.measures ?? []).length > 0
  || Boolean(dataset.operationType)
  || dataset.groupers.length > 0
  || (dataset.exposedColumns !== null && dataset.exposedColumns.length > 0);

const buildInitialDataset = (widget) => {
  if (!widget) return EMPTY_DATASET;
  // `builderConfig` is what `mapApiWidgetToUi` rebuilds from the saved
  // definition — the source, the filters, the operation, the groupers. Reading
  // only `widget.dataset` meant nothing set it and the Data step opened blank
  // on every edit: no entity marked, no filters, no operation.
  const saved = widget.builderConfig ?? widget.dataset ?? {};
  return {
    ...EMPTY_DATASET,
    ...saved,
    // A catalog widget always has a source, even when it carries no saved
    // dataset config, so edit mode never opens on an unconfigured Data step.
    sourceType: saved.sourceType ?? (saved.datasetId ? 'dataset' : 'entity'),
    // Only a widget saved through the Playground knows its source. A seeded
    // catalog row does not, and inventing one from `integration` — `salesforce`
    // is not an entity id — made `useSourceColumns` request it and 404, so the
    // Data step opened on a columns error with the Widget step locked. An empty
    // source is honest: the user re-picks it.
    sourceId: saved.sourceId ?? '',
    entityRef: saved.entityRef ?? '',
    // Without it a re-save would drop the composite and the endpoint would
    // reject the update.
    compositeEntityUuid: saved.compositeEntityUuid ?? '',
    operationType: saved.operationType ?? 'summarize',
    filters: saved.filters ?? [],
    /*
      Row ids are local React keys, and a hydrated row has none: the wire
      carries aggregations, not form rows. Minted here so editing the second
      measure of a reopened widget does not patch the first — `updateMeasure`
      matches on the id.
    */
    measures: (saved.measures ?? []).map((measure) => ({ id: rowId('msr'), ...measure })),
    // Same reason, and one more since fix/widgets: a binding slot answered
    // with a grouper stores that row's id.
    groupers: (saved.groupers ?? []).map((grouper) => ({ id: rowId('grp'), ...grouper })),
    exposedColumns: saved.exposedColumns ?? null,
  };
};

/**
 * The Playground's single source of truth (CT-5190, extended by CT-5191..5195).
 *
 * Every step component reads its gate from here; none computes its own. Nothing
 * derived is stored in `useState` — `dataShape`, the validity flags, `canSave`,
 * `saveBlockedReason` and `isDirty` are all `useMemo` over the raw fields, so an
 * earlier step going invalid re-locks everything after it in the same render
 * rather than through a cascade of effects.
 *
 * **The reset contract (CT-5191).** Changing the source, dataset or entity
 * invalidates the filters, the operation and the type underneath it. Rather than
 * silently discarding that work, `requestSourceType` / `requestDataset` /
 * `requestEntity` stage the change in `pendingSourceChange` whenever there is
 * something to lose; the caller shows the warning and then commits or cancels.
 * With nothing downstream configured they apply immediately — a confirm dialog
 * for a no-op is noise.
 *
 * @param {object} [initialWidget] Hydration source in edit mode. Pass it only
 *   once the widget has loaded, or `isDirty` compares against the wrong snapshot.
 */
export const useWidgetBuilderForm = (initialWidget = null) => {
  const initialDataset = useMemo(() => buildInitialDataset(initialWidget), [initialWidget]);
  const initialType = initialWidget?.type ?? '';
  const initialName = initialWidget?.name ?? '';
  const initialDescription = initialWidget?.description ?? '';

  const [activeTab, setActiveTab] = useState(FIRST_WIDGET_BUILDER_TAB);
  const [dataset, setDataset] = useState(initialDataset);
  const [type, setTypeState] = useState(initialType);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [endUserFiltersEnabled, setEndUserFiltersEnabled] = useState(
    Boolean(initialWidget?.endUserFiltersEnabled),
  );
  // Persisted with the widget as its `size`, so it belongs on the form rather
  // than staying local to the preview pane.
  const [previewSize, setPreviewSize] = useState(initialWidget?.size ?? 'medium');
  // `{ slot: fieldUuid }` — which exposed column fills each of the selected
  // type's binding slots. Field uuids, like every other selection; the payload
  // translates them into query aliases. Kept out of `dataset` on purpose: a
  // slot belongs to the **type**, and a change of type invalidates it while
  // leaving the data configuration alone.
  /*
    Derived rather than inlined into `useState`: the dirty check compares the
    current state against this same object, so a widget whose measure slots
    were restored must not open reading as unsaved work.

    The service resolves the column slots — it is the only place that still
    has the record set's alias → field uuid map — and `restoreSlotAssignments`
    answers the measure and dimension ones, which only exist as form rows.

    On a dataset neither of those applies: there are no rows, and the alias
    the binding names is the dataset's own `fieldKey`. Its answers go last
    because they are read from the thing the widget was actually built on
    (CT-5860).
  */
  const initialSlotAssignments = useMemo(() => ({
    ...(initialWidget?.slotAssignments ?? EMPTY_SLOTS),
    ...restoreSlotAssignments({
      bindings: initialWidget?.bindings ?? null,
      measures: initialDataset.measures,
      groupers: initialDataset.groupers,
    }),
    ...restoreDatasetSlotAssignments({
      bindings: initialWidget?.bindings ?? null,
      columns: initialWidget?.datasetRef?.columns ?? [],
    }),
  }), [initialDataset.groupers, initialDataset.measures, initialWidget]);
  const [slotAssignments, setSlotAssignments] = useState(initialSlotAssignments);
  /*
    The DATASET the Data step's selection was published as (CT-5725):
    `{ datasetUuid, columns: [{ fieldUuid, fieldKey, isRowKey }], signature }`.
    The widget is created on top of it, and its `fieldKey`s are the only
    names the bindings may use. Held here rather than in `dataset` because it
    is not something the author set: it is what the backend answered for what
    they set, and `usePublishedDataset` refreshes it when the selection moves.
  */
  const [datasetRef, setDatasetRef] = useState(initialWidget?.datasetRef ?? null);
  /*
    The bindings that bind no field: `time`, `point_mode`, `source_mode`
    (fix/widgets). Kept beside `slotAssignments` and invalidated by the same
    change of type — a correlation's `point_mode` means nothing to a KPI.

    `time.ref` points at a Data-step element, like any other answer; the
    payload resolves it to the alias the generated dataset carries. Reading a
    saved one back needs the opposite resolution, which only the payload side
    can do, so what comes back here is the stored alias and the editor shows
    it once the pools agree.
  */
  const initialNonFieldBindings = useMemo(() => {
    /*
      `time.field` was stored as the alias of a Data-step element, and the
      form holds refs. The service already resolved the record-set aliases to
      field uuids, and a grouper's alias is positional (`dim_2`), so between
      the two the row is found; anything else is kept verbatim, which the
      payload sends back as it came rather than dropping the period.
    */
    const refOf = (alias) => {
      const fromColumns = initialWidget?.slotAssignments?.time;
      if (fromColumns) return fromColumns;
      const at = /^dim_(\d+)$/.exec(alias ?? '');
      const row = at ? initialDataset.groupers[Number(at[1]) - 1] : null;
      return row?.id || alias;
    };
    return parseNonFieldBindings(initialWidget?.bindings ?? null, refOf);
  }, [initialDataset.groupers, initialWidget]);
  const [nonFieldBindings, setNonFieldBindings] = useState(initialNonFieldBindings);
  // `{ path: value }` — the type's own look-and-feel options, flat so a nested
  // one (`empty_state.title`) compares and resets like any other. Kept beside
  // `slotAssignments` for the same reason: both belong to the **type**, not to
  // the data, and both are invalidated by a change of type (CT-5579).
  const [displaySettings, setDisplaySettings] = useState(
    () => fromDisplayConfig(initialWidget?.display),
  );
  // One staged change at a time — a source switch and an operation switch cannot
  // both be awaiting confirmation, and modelling them as one keeps the modal to
  // a single instance.
  const [pendingChange, setPendingChange] = useState(null);

  const dataShape = useMemo(() => resolveDataShape(dataset), [dataset]);

  const isDataStepValid = useMemo(() => {
    if (!dataset.sourceType || !dataset.sourceId) return false;
    if (dataset.sourceType === 'dataset') return Boolean(dataset.datasetId);
    if (!dataset.entityRef) return false;
    /*
      The selection is published as a plain record set (CT-5725), so this
      step is done once it says which columns travel. What is done with them
      — the operations, the groupings — belongs to the type's slots and is
      asked in Configure, so there is no operation left to choose here.
    */
    return dataset.exposedColumns?.length > 0;
  }, [dataset]);

  const isWidgetStepValid = useMemo(
    () => Boolean(type) && name.trim().length > 0,
    [name, type],
  );

  const canSave = isDataStepValid && isWidgetStepValid;

  const saveBlockedReason = useMemo(() => {
    if (!dataset.sourceId) return 'widgetBuilder.save.blockedNoSource';
    if (!isDataStepValid) return 'widgetBuilder.save.blockedIncompleteDataset';
    if (!type) return 'widgetBuilder.save.blockedNoType';
    if (name.trim().length === 0) return 'widgetBuilder.save.blockedNoName';
    return null;
  }, [dataset.sourceId, isDataStepValid, name, type]);

  /**
   * Label for the primary action while it is blocked. The prototype relabels the
   * button rather than only dimming it, so the next thing to do is readable
   * without hovering.
   */
  const saveLabelKey = useMemo(() => {
    if (canSave) return 'widgetBuilder.create.save';
    if (!dataset.sourceId) return 'widgetBuilder.save.labelSetUpData';
    if (!isDataStepValid) return 'widgetBuilder.save.labelCompleteDataConfig';
    if (!type) return 'widgetBuilder.save.labelChooseType';
    return 'widgetBuilder.save.labelNameWidget';
  }, [canSave, dataset.sourceId, isDataStepValid, type]);

  /**
   * Everything a save persists, as a comparable string.
   *
   * Row ids are stripped: they are local React keys, re-issued on every
   * hydration, so leaving them in would make a freshly loaded form differ from
   * itself.
   */
  const snapshotOf = useCallback(
    (values) => JSON.stringify({
      dataset: withoutRowIds(values.dataset),
      type: values.type,
      name: values.name,
      description: values.description,
      endUserFiltersEnabled: values.endUserFiltersEnabled,
      previewSize: values.previewSize,
      // Sorted: an assignment made in a different order is the same widget,
      // and object key order would otherwise report it as unsaved work.
      slotAssignments: Object.fromEntries(
        Object.entries(values.slotAssignments ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      ),
      nonFieldBindings: Object.fromEntries(
        Object.entries(values.nonFieldBindings ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      ),
      displaySettings: Object.fromEntries(
        Object.entries(values.displaySettings ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      ),
    }),
    [],
  );

  const currentSnapshot = useMemo(
    () => snapshotOf({
      dataset,
      type,
      name,
      description,
      endUserFiltersEnabled,
      previewSize,
      slotAssignments,
      nonFieldBindings,
      displaySettings,
    }),
    [
      dataset,
      description,
      displaySettings,
      endUserFiltersEnabled,
      name,
      nonFieldBindings,
      previewSize,
      slotAssignments,
      snapshotOf,
      type,
    ],
  );

  const initialSnapshot = useMemo(
    () => snapshotOf({
      dataset: initialDataset,
      type: initialType,
      name: initialName,
      description: initialDescription,
      endUserFiltersEnabled: Boolean(initialWidget?.endUserFiltersEnabled),
      previewSize: initialWidget?.size ?? 'medium',
      slotAssignments: initialSlotAssignments,
      nonFieldBindings: initialNonFieldBindings,
      displaySettings: fromDisplayConfig(initialWidget?.display),
    }),
    [initialDataset, initialDescription, initialName, initialNonFieldBindings,
      initialSlotAssignments, initialType, initialWidget, snapshotOf],
  );

  // Set on a successful save, so a widget created in this session stops
  // comparing itself to the empty form it started from. Without it `Cancel`
  // after `Keep editing` claims an unsaved widget that is saved.
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const isDirty = currentSnapshot !== (savedSnapshot ?? initialSnapshot);

  // Narrower than `isDirty`: only an unsaved change to the **query** — the
  // source, the operation, the columns, the filters, the groupers. That is all
  // `widget/execute` runs, so it is what decides whether the stored result
  // still describes the preview. Size, name, description and type change how
  // the result is drawn, not what it is; counting them made switching the
  // preview to S or L drop the real data for the sample (CT-5183).
  const isDataDirty = useMemo(() => {
    const baseline = JSON.parse(savedSnapshot ?? initialSnapshot).dataset;
    return JSON.stringify(withoutRowIds(dataset)) !== JSON.stringify(baseline);
  }, [dataset, initialSnapshot, savedSnapshot]);

  /** Re-baseline after a save. */
  const markSaved = useCallback(() => setSavedSnapshot(currentSnapshot), [currentSnapshot]);

  // ─── Source selection, with the reset contract ────────────────────────────

  /** Apply a staged change and clear everything that depended on it. */
  const applyPendingChange = useCallback((change) => {
    if (change.kind === 'operation') {
      const operationType = change.patch.operationType;
      setDataset((current) => ({
        ...current,
        operationType,
        // The two operations produce different shapes, so what the other one
        // filled no longer means anything. Filters survive: they narrow the
        // source, not the operation.
        groupers: operationType === 'summarize' ? current.groupers : [],
        exposedColumns: operationType === 'record_set' ? current.exposedColumns : null,
      }));
    } else {
      setDataset((current) => ({
        ...current,
        ...change.patch,
        filters: [],
        groupers: [],
        exposedColumns: null,
      }));
    }
    // The widget type survives on purpose: every type is offered against every
    // source, so a change of source leaves nothing about it to invalidate. Its
    // **slots** do not: they name columns, and both branches above wipe the
    // columns those names pointed at.
    setSlotAssignments(EMPTY_SLOTS);
    // The period names a column too, so it goes with them. The mode enums
    // (`point_mode`, `source_mode`) belong to the type, not to the data, and
    // survive — only `time` is dropped.
    setNonFieldBindings((current) => (
      'time' in current
        ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== 'time'))
        : current
    ));
    setPendingChange(null);
  }, []);

  /**
   * Stage a source change, or apply it immediately when nothing is lost.
   * @param {object} patch Fields to write onto the dataset.
   */
  const requestSourcePatch = useCallback(
    (patch) => {
      const change = { kind: 'source', patch, clears: DOWNSTREAM_CONFIG_KEYS };
      // Depends on the live `dataset` rather than a ref: reading a ref during
      // render is not allowed, and the identity churn is harmless here — the
      // pickers render four datasets and eight entities, not a long list.
      if (hasDownstreamConfig(dataset)) {
        setPendingChange(change);
        return;
      }
      applyPendingChange(change);
    },
    [applyPendingChange, dataset],
  );

  const requestSourceType = useCallback(
    (sourceType) => {
      // Re-clicking the type already in effect is a no-op, not a reset.
      // `PickerOption` fires `onSelect` on every click, and with nothing
      // downstream configured the wipe below applies immediately — so clicking
      // `Entity` twice would silently clear the entity just chosen.
      if (sourceType === dataset.sourceType) return;
      // Switching between "existing dataset" and "raw entity" resets the whole
      // path, not just the selection.
      requestSourcePatch({
        sourceType,
        sourceId: '',
        sourceLabel: '',
        sourceIntegration: '',
        datasetId: '',
        datasetShape: '',
        entityRef: '',
      });
    },
    [dataset.sourceType, requestSourcePatch],
  );

  const requestDataset = useCallback(
    (datasetId, datasetShape = '', sourceLabel = '') => {
      requestSourcePatch({
        sourceType: 'dataset',
        sourceId: datasetId,
        sourceLabel,
        sourceIntegration: '',
        datasetId,
        datasetShape,
        entityRef: '',
        compositeEntityUuid: '',
      });
    },
    [requestSourcePatch],
  );

  const requestEntity = useCallback(
    (entityRef, sourceLabel = '', sourceIntegration = '', compositeEntityUuid = '') => {
      requestSourcePatch({
        sourceType: 'entity',
        sourceId: entityRef,
        sourceLabel,
        sourceIntegration,
        entityRef,
        compositeEntityUuid,
        datasetId: '',
        datasetShape: '',
        // The dataset is always a record set now, and the author is no
        // longer asked: the aggregations live in the widget's bindings.
        operationType: 'record_set',
      });
    },
    [requestSourcePatch],
  );

  const confirmPendingChange = useCallback(() => {
    if (pendingChange) applyPendingChange(pendingChange);
  }, [applyPendingChange, pendingChange]);

  const cancelPendingChange = useCallback(() => setPendingChange(null), []);

  // ─── Filters (CT-5193) ────────────────────────────────────────────────────

  const addFilter = useCallback(() => {
    setDataset((current) => ({
      ...current,
      filters: [...current.filters, { id: rowId('flt'), column: '', operator: '', value: '', valueTo: '' }],
    }));
  }, []);

  const updateFilter = useCallback((id, patch) => {
    setDataset((current) => ({
      ...current,
      filters: current.filters.map((filter) => {
        if (filter.id !== id) return filter;
        // Changing the column invalidates the operator (its set comes from the
        // column's type) and the value (its control does too). Keeping either
        // one is the bug this reset exists to prevent.
        //
        // `patch` spreads **last** on purpose: the row hands over the new type's
        // default operator in the same patch, and spreading the reset after it
        // threw that away — the select fell back to its placeholder on every
        // column pick.
        if ('column' in patch && patch.column !== filter.column) {
          return { ...filter, operator: '', value: '', valueTo: '', ...patch };
        }
        // `is empty` / `is not empty` take no value, so any typed one is dropped
        // rather than kept invisibly and sent on save.
        if ('operator' in patch && OPERATORS_WITHOUT_VALUE.includes(patch.operator)) {
          return { ...filter, ...patch, value: '', valueTo: '' };
        }
        return { ...filter, ...patch };
      }),
    }));
  }, []);

  const removeFilter = useCallback((id) => {
    setDataset((current) => ({
      ...current,
      filters: current.filters.filter((filter) => filter.id !== id),
    }));
  }, []);

  /** Only complete filters reach the preview and the save payload. */
  const validFilters = useMemo(
    () => dataset.filters.filter(isCompleteFilter),
    [dataset.filters],
  );

  const validMeasures = useMemo(
    () => (dataset.measures ?? []).filter(isCompleteMeasure),
    [dataset.measures],
  );

  // ─── Operation (CT-5194) ──────────────────────────────────────────────────

  /**
   * Switch Summarize ↔ Record Set, warning first when the other path already has
   * work in it. Choosing an operation for the first time applies straight away —
   * a confirm dialog for a no-op is noise.
   */
  const requestOperation = useCallback(
    (operationType) => {
      if (operationType === dataset.operationType) return;
      const change = {
        kind: 'operation',
        patch: { operationType },
        clears: OPERATION_CONFIG_KEYS,
      };
      const hasPathConfig =
        dataset.groupers.length > 0
        || (dataset.exposedColumns !== null && dataset.exposedColumns.length > 0);
      if (dataset.operationType && hasPathConfig) {
        setPendingChange(change);
        return;
      }
      applyPendingChange(change);
    },
    [applyPendingChange, dataset],
  );

  /*
    A measure is an operation and, unless it counts rows, a column. Added
    empty so the author picks the operation first: which columns are on
    offer depends on it (`columnFitsSlotModel`).
  */
  const addMeasure = useCallback(() => {
    setDataset((current) => ({
      ...current,
      measures: [...(current.measures ?? []), { id: rowId('msr'), aggregate: '', column: '' }],
    }));
  }, []);

  const updateMeasure = useCallback((id, patch) => {
    setDataset((current) => ({
      ...current,
      measures: (current.measures ?? []).map((measure) => (
        measure.id === id ? { ...measure, ...patch } : measure)),
    }));
  }, []);

  const removeMeasure = useCallback((id) => {
    setDataset((current) => ({
      ...current,
      measures: (current.measures ?? []).filter((measure) => measure.id !== id),
    }));
  }, []);

  const addGrouper = useCallback(() => {
    setDataset((current) => ({
      ...current,
      groupers: [...current.groupers, { id: rowId('grp'), column: '' }],
    }));
  }, []);

  const updateGrouper = useCallback((id, patch) => {
    setDataset((current) => ({
      ...current,
      groupers: current.groupers.map((grouper) => (grouper.id === id ? { ...grouper, ...patch } : grouper)),
    }));
  }, []);

  const removeGrouper = useCallback((id) => {
    setDataset((current) => ({
      ...current,
      groupers: current.groupers.filter((grouper) => grouper.id !== id),
    }));
  }, []);

  const setExposedColumns = useCallback((fieldUuids) => {
    setDataset((current) => ({ ...current, exposedColumns: fieldUuids }));
    // A slot can only point at a column the widget actually exposes: the
    // contract binds the query's aliases, and an unexposed column has none.
    // Dropping it here is what makes the Configure step ask again, rather than
    // the backend answering with `UI_PRESENTATION_BINDING_FIELD_MISSING`.
    const exposed = new Set(fieldUuids ?? []);
    setSlotAssignments((current) => {
      // A list slot keeps the columns that are still exposed, in the author's
      // order, and only disappears once none of them is: dropping the whole
      // answer because one column left is how `identity` and `card_fields`
      // used to be silently refilled by the automatic assignment.
      const entries = Object.entries(current).map(([slot, value]) => {
        if (!Array.isArray(value)) return [slot, exposed.has(value) ? value : null];
        const kept = value.filter((fieldUuid) => exposed.has(fieldUuid));
        return [slot, kept.length ? kept : null];
      });
      const changed = entries.some(([slot, value]) => (
        value === null || (Array.isArray(value) && value.length !== current[slot].length)
      ));
      if (!changed) return current;
      return Object.fromEntries(entries.filter(([, value]) => value !== null));
    });
  }, []);


  const validGroupers = useMemo(
    () => dataset.groupers.filter((grouper) => Boolean(grouper.column)),
    [dataset.groupers],
  );

  // ─── Widget ───────────────────────────────────────────────────────────────

  const setType = useCallback(
    (next) => {
      // Re-picking the tile already selected is a no-op, not a reset: the
      // slots below would otherwise be wiped by a click that changed nothing.
      if (next === type) return;
      setTypeState(next);
      // Slots belong to the type — `lane` means nothing to a carousel — so a
      // stale assignment must not ride along into the next contract. The same
      // goes for the display options: a donut's `inner_radius_pct` is not a
      // table's, and `additionalProperties: false` refuses a stray one.
      setSlotAssignments(EMPTY_SLOTS);
      setNonFieldBindings(EMPTY_SLOTS);
      setDisplaySettings(EMPTY_SLOTS);
    },
    [type],
  );

  /**
   * Answer one binding slot. The value is a **ref** — a column's field uuid, a
   * measure's or a grouper's row id (see `widgetSlotPools`) — or a list of
   * them for a slot the contract declares as an array. An empty value clears
   * the answer, which hands the slot back to the automatic assignment.
   */
  const setSlotAssignment = useCallback((slot, ref) => {
    setSlotAssignments((current) => {
      const isEmpty = Array.isArray(ref) ? ref.length === 0 : !ref;
      if (isEmpty) {
        if (!(slot in current)) return current;
        return Object.fromEntries(Object.entries(current).filter(([key]) => key !== slot));
      }
      if (current[slot] === ref) return current;
      if (Array.isArray(ref) && Array.isArray(current[slot])
        && current[slot].length === ref.length
        && current[slot].every((value, index) => value === ref[index])) return current;
      return { ...current, [slot]: ref };
    });
  }, []);

  /**
   * Set one non-field binding — the period, or one of the two mode enums.
   * An empty value removes it, which leaves the key out of the contract.
   */
  const setNonFieldBinding = useCallback((key, value) => {
    setNonFieldBindings((current) => {
      const isEmpty = value === '' || value === null || value === undefined;
      if (isEmpty) {
        if (!(key in current)) return current;
        return Object.fromEntries(Object.entries(current).filter(([entry]) => entry !== key));
      }
      if (current[key] === value) return current;
      return { ...current, [key]: value };
    });
  }, []);

  /**
   * Set one display option; an empty value removes the override.
   *
   * Removing rather than storing `''` is what keeps "never touched" and "set
   * to nothing" the same thing, which they are: no property in any of the 28
   * schemas declares a `default`, so an absent one is the backend's to decide.
   */
  const setDisplaySetting = useCallback((path, value) => {
    setDisplaySettings((current) => {
      if (value === '' || value === null || value === undefined) {
        if (!(path in current)) return current;
        return Object.fromEntries(Object.entries(current).filter(([key]) => key !== path));
      }
      if (current[path] === value) return current;
      return { ...current, [path]: value };
    });
  }, []);

  // ─── Tabs ─────────────────────────────────────────────────────────────────

  // Free navigation: a tab is a view of the same form, not a step in a wizard,
  // so nothing here locks one. What is gated is the **save** — see `canSave`.
  const goToTab = useCallback((tabId) => setActiveTab(tabId), []);

  return useMemo(
    () => ({
      activeTab,
      goToTab,

      dataset,
      type,
      name,
      description,
      endUserFiltersEnabled,
      previewSize,

      dataShape,
      isDataStepValid,
      isWidgetStepValid,
      canSave,
      saveBlockedReason,
      saveLabelKey,
      isDirty,
      isDataDirty,
      markSaved,
      validFilters,
      validMeasures,
      validGroupers,

      slotAssignments,
      setSlotAssignment,
      datasetRef,
      setDatasetRef,
      nonFieldBindings,
      setNonFieldBinding,
      displaySettings,
      setDisplaySetting,

      pendingChange,
      requestSourceType,
      requestDataset,
      requestEntity,
      requestOperation,
      confirmPendingChange,
      cancelPendingChange,

      addFilter,
      updateFilter,
      removeFilter,

      addMeasure,
      updateMeasure,
      removeMeasure,
      addGrouper,
      updateGrouper,
      removeGrouper,
      setExposedColumns,

      setType,
      setName,
      setDescription,
      setEndUserFiltersEnabled,
      setPreviewSize,
    }),
    [
      addFilter,
      addMeasure,
      updateMeasure,
      removeMeasure,
      addGrouper,
      canSave,
      cancelPendingChange,
      confirmPendingChange,
      dataShape,
      dataset,
      description,
      endUserFiltersEnabled,
      activeTab,
      goToTab,
      isDataStepValid,
      isDataDirty,
      isDirty,
      isWidgetStepValid,
      markSaved,
      name,
      pendingChange,
      previewSize,
      removeFilter,
      removeGrouper,
      requestDataset,
      requestEntity,
      requestOperation,
      requestSourceType,
      saveBlockedReason,
      saveLabelKey,
      setDescription,
      setEndUserFiltersEnabled,
      setExposedColumns,
      setName,
      setSlotAssignment,
      setNonFieldBinding,
      datasetRef,
      setType,
      displaySettings,
      setDisplaySetting,
      slotAssignments,
      nonFieldBindings,
      type,
      updateFilter,
      updateGrouper,
      validFilters,
      validMeasures,
      validGroupers,
    ],
  );
};

export default useWidgetBuilderForm;
